/**
 * "自分のタスク" — Phase 2.1 personal task focus view.
 *
 * Surfaces only the work assigned to the signed-in user, sliced into
 * four tabs (今日 / 今週 / 待たせ中 / 完了済 14日). Pulls WBS rows and
 * migration tasks from Firestore through `useMyTasks`, leaves the
 * normalised+filtered shape from `lib/myTasks.ts` to drive rendering.
 *
 * The page is intentionally separated into two columns at md+ widths:
 *   left: tab strip + scrolling list
 *   right: contextual detail panel (WBS or migration task, source-aware)
 *
 * On mobile (< md) the detail panel becomes an overlay sliding over
 * the list so a tapped row gets the full screen.
 */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Settings as SettingsIcon } from 'lucide-react';
import type { TaskDoc, WbsItemDoc } from '@mct/shared';
import {
  useAssignees,
  useProject,
  useRuns,
  useTasks,
} from '../hooks/useFirestoreData';
import { useWbsItems } from '../hooks/useWbs';
import { useMyTasks } from '../hooks/useMyTasks';
import { Spinner } from '../components/ui/Spinner';
import { MyTaskRow } from '../components/MyTasks/MyTaskRow';
import { WbsItemDetailPanel } from '../components/Wbs/WbsItemDetailPanel';
import { TaskDetailPanel } from '../components/TaskDetail/TaskDetailPanel';
import { updateWbsItem } from '../lib/wbs';
import { deleteTask } from '../lib/firestore';
import { getDb } from '../lib/firebase';
import { buildProgressPatch } from '../lib/myTasks';
import { cn } from '../lib/utils';

type TabId = 'today' | 'thisWeek' | 'blockedByMe' | 'doneRecent';

const TAB_LABELS: Record<TabId, string> = {
  today: '今日',
  thisWeek: '今週',
  blockedByMe: '待たせ中',
  doneRecent: '完了済',
};

export function MyTasksPage() {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId) return null;
  return <MyTasksContent projectId={projectId} />;
}

function MyTasksContent({ projectId }: { projectId: string }) {
  // `now` is captured once per render — the buckets re-derive from the
  // raw items, so a stale "now" between renders is fine. We don't tick
  // it, which keeps the day boundary stable across a single visit.
  const now = useMemo(() => new Date(), []);
  const { data: project } = useProject(projectId);
  const { data: assignees } = useAssignees(projectId);
  const { data: wbsItems } = useWbsItems(projectId);
  const { data: tasks } = useTasks(projectId);
  const { data: runs } = useRuns(projectId);

  const { buckets, mappingsReady, mapped, loading } = useMyTasks(
    projectId,
    now,
  );

  const [activeTab, setActiveTab] = useState<TabId>('today');
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [selectedSource, setSelectedSource] = useState<'wbs' | 'task' | null>(
    null,
  );
  const [mobileOverlayOpen, setMobileOverlayOpen] = useState(false);

  const tabRows = (() => {
    switch (activeTab) {
      case 'today':
        return buckets.today;
      case 'thisWeek':
        return buckets.thisWeek;
      case 'blockedByMe':
        return buckets.blockedByMe;
      case 'doneRecent':
        return buckets.doneRecent;
    }
  })();

  const counts: Record<TabId, number> = {
    today: buckets.today.length,
    thisWeek: buckets.thisWeek.length,
    blockedByMe: buckets.blockedByMe.length,
    doneRecent: buckets.doneRecent.length,
  };

  const selectedWbsItem: WbsItemDoc | null = useMemo(() => {
    if (selectedSource !== 'wbs' || !selectedRowId) return null;
    return wbsItems.find((i) => i.itemId === selectedRowId) ?? null;
  }, [selectedSource, selectedRowId, wbsItems]);

  const selectedTask: TaskDoc | null = useMemo(() => {
    if (selectedSource !== 'task' || !selectedRowId) return null;
    return tasks.find((t) => t.taskId === selectedRowId) ?? null;
  }, [selectedSource, selectedRowId, tasks]);

  const selectedRun = useMemo(() => {
    // Pick the active run if any, else the first by `order`. The detail
    // panel needs *some* run context to surface the per-execution form.
    const active = runs.find((r) => r.status === 'active');
    return active ?? runs[0] ?? null;
  }, [runs]);

  const onSelectRow = (id: string, source: 'wbs' | 'task') => {
    setSelectedRowId(id);
    setSelectedSource(source);
    setMobileOverlayOpen(true);
  };

  const onCloseDetail = () => {
    setSelectedRowId(null);
    setSelectedSource(null);
    setMobileOverlayOpen(false);
  };

  const adjustProgress = async (
    itemId: string,
    currentProgress: number,
    delta: number,
  ) => {
    // Patch shape (incl. status auto-promotion) lives in `lib/myTasks` so
    // it can be unit-tested without React. See `buildProgressPatch`.
    const patch = buildProgressPatch(currentProgress, delta);
    await updateWbsItem(getDb(), projectId, itemId, patch);
  };

  const completeWbsItem = async (itemId: string) => {
    await updateWbsItem(getDb(), projectId, itemId, {
      progress: 100,
      progressMode: 'manual',
      status: 'done',
      actualEnd: new Date(),
    });
  };

  // ---- empty / unmapped states --------------------------------------
  if (loading || !project) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (mappingsReady && !mapped) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 text-center text-sm text-slate-600">
        <p className="mb-2 text-base font-medium text-slate-800">
          自分のタスクを表示するには紐付けが必要です
        </p>
        <p className="mb-4 max-w-md text-slate-500">
          設定画面で自分の Firebase アカウントを担当者に紐付けてください。
          紐付けが完了すると、ここに自分担当の WBS 行と移行作業タスクがまとめて表示されます。
        </p>
        <Link
          to={`/projects/${projectId}/settings`}
          className="inline-flex items-center gap-1 rounded bg-slate-900 px-3 py-1.5 text-white hover:bg-slate-800"
        >
          <SettingsIcon className="h-4 w-4" />
          設定画面で自分をマッピング
        </Link>
      </div>
    );
  }

  return (
    <div className="relative flex h-full overflow-hidden">
      {/* List column */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div
          data-testid="my-tasks-tabbar"
          className="flex shrink-0 items-center gap-1 overflow-x-auto border-b border-slate-200 bg-white px-3 py-2 text-sm"
        >
          {(Object.keys(TAB_LABELS) as TabId[]).map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              data-testid={`my-tasks-tab-${id}`}
              data-active={activeTab === id ? '1' : '0'}
              className={cn(
                'inline-flex shrink-0 items-center gap-1.5 rounded px-3 py-1 transition-colors',
                activeTab === id
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-700 hover:bg-slate-100',
              )}
            >
              <span>{TAB_LABELS[id]}</span>
              <span
                className={cn(
                  'inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums',
                  activeTab === id ? 'bg-white text-slate-900' : 'bg-slate-200 text-slate-700',
                )}
              >
                {counts[id]}
              </span>
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-auto">
          {tabRows.length === 0 ? (
            <EmptyState tab={activeTab} />
          ) : (
            <ul data-testid="my-tasks-list" className="m-0 list-none p-0">
              {tabRows.map((row) => (
                <MyTaskRow
                  key={`${row.source}:${row.id}`}
                  row={row}
                  selected={selectedRowId === row.id && selectedSource === row.source}
                  onSelect={() => onSelectRow(row.id, row.source)}
                  onAdjustProgress={
                    row.source === 'wbs'
                      ? (delta) => adjustProgress(row.id, row.progress, delta)
                      : undefined
                  }
                  onComplete={
                    row.source === 'wbs'
                      ? () => completeWbsItem(row.id)
                      : undefined
                  }
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Detail panel — md+: right side; mobile: full-screen overlay. */}
      {selectedWbsItem ? (
        <DetailWrapper
          mobileOpen={mobileOverlayOpen}
          onClose={onCloseDetail}
        >
          <WbsItemDetailPanel
            project={project}
            item={selectedWbsItem}
            allItems={wbsItems}
            assignees={assignees}
            hasChildren={false}
            descendantIds={[]}
            onClose={onCloseDetail}
            onSelectItem={(id) => {
              setSelectedRowId(id);
              setSelectedSource('wbs');
            }}
          />
        </DetailWrapper>
      ) : selectedTask ? (
        <DetailWrapper
          mobileOpen={mobileOverlayOpen}
          onClose={onCloseDetail}
        >
          <TaskDetailPanel
            project={project}
            task={selectedTask}
            allTasks={tasks}
            currentRun={selectedRun}
            onClose={onCloseDetail}
            onSelectTask={(id) => {
              setSelectedRowId(id);
              setSelectedSource('task');
            }}
            onDelete={async () => {
              if (
                !window.confirm(
                  'このタスクを削除しますか？関連する実行レコードも削除されます。',
                )
              ) {
                return;
              }
              await deleteTask(getDb(), projectId, selectedTask.taskId);
              onCloseDetail();
            }}
          />
        </DetailWrapper>
      ) : (
        <aside className="hidden w-[420px] shrink-0 flex-col items-center justify-center border-l border-slate-200 bg-slate-50 px-6 text-center text-xs text-slate-500 md:flex">
          タスクを選んでください
        </aside>
      )}
    </div>
  );
}

function DetailWrapper({
  mobileOpen,
  onClose,
  children,
}: {
  mobileOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  // Single mount of `children`. The wrapper toggles between a fixed
  // full-screen overlay (mobile) and an inline right-pane (md+) via
  // Tailwind responsive classes. Rendering twice would mount the
  // detail panel — and any subscriptions inside it — twice, which we
  // don't want.
  return (
    <div
      data-testid="my-tasks-detail-wrapper"
      data-mobile-open={mobileOpen ? '1' : '0'}
      className={cn(
        // Mobile: full-screen overlay when open, hidden otherwise.
        'fixed inset-0 z-40 bg-white',
        mobileOpen ? 'flex' : 'hidden',
        // md+: revert to inline right-side pane regardless of `mobileOpen`.
        'md:static md:z-auto md:inset-auto md:flex md:w-auto',
      )}
    >
      <button
        type="button"
        aria-label="閉じる"
        className="absolute inset-y-0 left-0 w-2 bg-transparent md:hidden"
        onClick={onClose}
      />
      <div className="flex min-w-0 flex-1 overflow-hidden">{children}</div>
    </div>
  );
}

function EmptyState({ tab }: { tab: TabId }) {
  const message =
    tab === 'today'
      ? '今日が期限のタスクはありません 🎉'
      : tab === 'thisWeek'
        ? '今週中のタスクはありません'
        : tab === 'blockedByMe'
          ? '誰かを待たせているタスクはありません'
          : '直近 14 日の完了タスクはまだありません';
  return (
    <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-500">
      {message}
    </div>
  );
}
